/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import {
    Icon,
    FlexRow,
    FlexCol,
    Button,
    VisualOnlyLabel,
    DatePicker,
    Box,
    Text,
} from 'sarsaparilla';
import { gnoment, gnomentToCalendarDate, calendarDateToGnoment } from 'shared-ui/gnoment';
import {
    hasRule,
    readRuleValue,
    createAvailabilityRangeMap,
    getValidationErrorStyle,
} from '../../utils/utilsNew';
import { isExtendedStayPresent } from '../../utils/availability-utils-new';
import {
    DAYS_TO_SECS,
    IS_EXIT_DATE_EMPTY,
    OVERRIDE_STAY_LIMIT_MAX,
    DESOLATION_WILDERNESS_ID,
    ENCHANTMENT_ID,
    DINO_GREEN_YAMPA,
    SAN_JUAN_ID,
    AVAILABILITY_KEY_MAP_FORMAT,
    KING_RANGE_NG_ID,
} from '../../constants';
import ExtendedStay from './ExtendedStay';

export default class EntryExitDates extends React.Component {
    static propTypes = {
        isLottery: PropTypes.bool,
        launchDetail: PropTypes.any,
        handleLaunchDetailChange: PropTypes.func,
        validation: PropTypes.shape({}),
        permit: PropTypes.any,
        divisionId: PropTypes.string,
        isDisabled: PropTypes.bool,
        stayLimit: PropTypes.shape({
            value: PropTypes.number,
        }),
        stayMinimum: PropTypes.number,
        isQuotaModifyAllowed: PropTypes.bool,
        handleChangeQuotaDetails: PropTypes.func,
        isBypassRulesValidations: PropTypes.bool,
        onFormValidate: PropTypes.func,
        addFieldRef: PropTypes.any,
        componentName: PropTypes.string,
    };

    static defaultProps = {
        isDisabled: false,
        permit: {},
        divisionId: '',
        stayLimit: {},
        stayMinimum: 0,
        isQuotaModifyAllowed: false,
        isBypassRulesValidations: false,
        onFormValidate: () => {},
    };

    constructor(props) {
        super(props);
        this.handleEntryDateChange = this.handleEntryDateChange.bind(this);
        this.handleExitDateChange = this.handleExitDateChange.bind(this);
        this.handleExtendedStayChange = this.handleExtendedStayChange.bind(this);

        this.computeStayLimit = this.computeStayLimit.bind(this);
        this.computeExitLimit = this.computeExitLimit.bind(this);
    }

    handleExtendedStayChange() {
        const { isExtended } = this.props.launchDetail;
        const newDetail = { ...this.props.launchDetail };
        newDetail.isExtended = !isExtended;

        const exitLimit = this.computeExitLimit(
            this.props.launchDetail.entryDate,
            this.props.divisionId
        );
        if (
            this.props.launchDetail.exitDate &&
            this.props.launchDetail.exitDate.isAfter(exitLimit)
        ) {
            newDetail.exitDate = exitLimit;
        }

        // DINOSAUR GREEN YAMPA REQUIREMENTS
        if (this.props.permit.id === DINO_GREEN_YAMPA) newDetail.exitDate = undefined;

        this.props.handleLaunchDetailChange(newDetail);
    }

    handleExitDateChange(date) {
        const newDetail = { ...this.props.launchDetail };
        newDetail.exitDate = date;
        this.props.handleLaunchDetailChange(newDetail);
    }

    handleEntryDateChange(date) {
        const newDetail = { ...this.props.launchDetail };
        newDetail.entryDate = date;
        this.props.handleLaunchDetailChange(newDetail);
    }

    computeStayLimit(divisionId) {
        let stayLimit = 365 * DAYS_TO_SECS;

        stayLimit = this.props.stayLimit.value;

        if (
            hasRule('StayLimitOverLengthInDays', this.props.permit.rules, divisionId) &&
            this.props.launchDetail.isExtended
        ) {
            stayLimit +=
                readRuleValue(
                    'StayLimitOverLengthInDays',
                    this.props.permit.rules,
                    divisionId
                ) * DAYS_TO_SECS;
        }
        if (this.props.isBypassRulesValidations) {
            stayLimit = OVERRIDE_STAY_LIMIT_MAX * DAYS_TO_SECS;
        }
        return stayLimit;
    }

    computeExitLimit(entryDate, divisionId) {
        const stayLimit = this.computeStayLimit(divisionId);
        const newMoment = gnoment(entryDate)
            .utc()
            .clone()
            .add(stayLimit, 'seconds')
            .add(-1, 'days');
        return newMoment;
    }

    renderChangeDetails() {
        if (this.props.isLottery && this.props.permit.id === ENCHANTMENT_ID) {
            return null;
        }

        if (this.props.permit.id === SAN_JUAN_ID) {
            return null;
        }

        if (this.props.isQuotaModifyAllowed) {
            return (
                <div className="rec-form-item-wrap">
                    <Button
                        type="button"
                        appearance="link"
                        screenReaderTextBefore="Redirect to detailed availability to modify dates of current reservation"
                        iconBeforeElement={<Icon iconName="edit" />}
                        onClick={this.props.handleChangeQuotaDetails}
                    >
                        Modify Trip
                    </Button>
                </div>
            );
        }
        return null;
    }

    renderTotalDays() {
        const { entryDate } = this.props.launchDetail;

        if (this.props.launchDetail.exitDate) {
            return this.props.launchDetail.exitDate.diff(entryDate, 'days') + 1;
        }

        return null;
    }

    renderExtendedStay() {
        if (isExtendedStayPresent(this.props.permit)) {
            return (
                <ExtendedStay
                    isDisabled={this.props.isDisabled}
                    selected={this.props.launchDetail.isExtended}
                    onSelectionChange={this.handleExtendedStayChange}
                />
            );
        }
        return undefined;
    }

    renderExitDateCalendar(entryDate, exitDate, newMap) {
        const recFormStyle =
            getValidationErrorStyle(
                this.props.validation,
                IS_EXIT_DATE_EMPTY,
                'rec-form-item-wrap rec-error'
            ) || 'rec-form-item-wrap';
        const isExitDateEmptyCheck =
            this.props.validation && this.props.validation[IS_EXIT_DATE_EMPTY]
                ? this.props.validation[IS_EXIT_DATE_EMPTY]
                : null;
        const initialVisibleMonth = exitDate || entryDate;
        const validationMessage =
            isExitDateEmptyCheck && this.props.validation[IS_EXIT_DATE_EMPTY]?.message
                ? this.props.validation[IS_EXIT_DATE_EMPTY]?.message
                : isExitDateEmptyCheck;

        const isDayBlockedIQR = (day) => !newMap[day.format(AVAILABILITY_KEY_MAP_FORMAT)];
        const getDayTypeIQR = (day) => (isDayBlockedIQR(day) ? 'blocked' : 'available');

        //To make King Range exit empty by default.  Todo: analyze why value can't just be used like for Paria
        const exitDateWithValue = this.props.permit?.id !== KING_RANGE_NG_ID;

        return (
            <div className={recFormStyle}>
                {exitDateWithValue ? (
                    <DatePicker
                        label="Exit Date"
                        ref={this.props.addFieldRef?.(this.props.componentName, 0)}
                        id="exitDateCalendar"
                        value={!isEmpty(newMap) ? gnomentToCalendarDate(exitDate) : null}
                        onChange={(date) =>
                            date &&
                            this.handleExitDateChange(
                                calendarDateToGnoment(date).utc(true),
                                'exitDateCalendar'
                            )
                        }
                        numVisibleMonths={1}
                        isDateUnavailable={(day) =>
                            isDayBlockedIQR(calendarDateToGnoment(day))
                        }
                        getDayType={(day) => getDayTypeIQR(calendarDateToGnoment(day))}
                        placeholderValue={
                            initialVisibleMonth
                                ? gnomentToCalendarDate(initialVisibleMonth.utc())
                                : null
                        }
                        showClearDates={false}
                        isDisabled={this.props.isDisabled}
                        isRequired={true}
                    />
                ) : (
                    <DatePicker
                        label="Exit Date"
                        ref={this.props.addFieldRef?.(this.props.componentName, 0)}
                        id="exitDateCalendar"
                        defaultValue={
                            !isEmpty(newMap) ? gnomentToCalendarDate(exitDate) : null
                        }
                        onChange={(date) =>
                            date &&
                            this.handleExitDateChange(
                                calendarDateToGnoment(date).utc(true),
                                'exitDateCalendar'
                            )
                        }
                        placeholderValue={
                            initialVisibleMonth
                                ? gnomentToCalendarDate(initialVisibleMonth.utc())
                                : null
                        }
                        numVisibleMonths={1}
                        isDateUnavailable={(day) =>
                            isDayBlockedIQR(calendarDateToGnoment(day))
                        }
                        getDayType={(day) => getDayTypeIQR(calendarDateToGnoment(day))}
                        showClearDates={false}
                        isDisabled={this.props.isDisabled}
                        isRequired={true}
                    />
                )}
                {recFormStyle !== 'rec-form-item-wrap' && validationMessage}
            </div>
        );
    }

    render() {
        // Stay limit TODOs:
        // 1) Eventually, we will want to control the stay limit based on group size and if
        // the date is in high use season. At time of writing, this is checked in the backend only.
        // 2) For other permits, end date will be restricted by availability
        const entryDate = gnoment(this.props.launchDetail.entryDate).utc();
        const exitLimit = this.computeExitLimit(entryDate, this.props.divisionId);
        const exitDate = this.props.launchDetail.exitDate
            ? gnoment(this.props.launchDetail.exitDate).utc()
            : undefined;
        const stayMinimum = this.props.stayMinimum / DAYS_TO_SECS;

        const description = this.props.isDisabled ? null : (
            <p>Enter the exit date you will be leaving this location.</p>
        );

        // clone here to avoid mutating entryDate
        // Requirement for dino yampa only if the customer chooses an extended date
        // clone exit limit in order to avoid mutating

        // DINOSAUR GREEN YAMPA REQUIREMENTS
        const biasedEntryDate =
            this.props.permit.id === DINO_GREEN_YAMPA &&
            this.props.launchDetail.isExtended
                ? exitLimit.clone().subtract(1, 'days')
                : entryDate;
        // =======================================

        const newMap = createAvailabilityRangeMap(
            biasedEntryDate,
            exitLimit,
            stayMinimum
        );

        // Leaving for just desolation for now. Can figure out if this needs to be added for other permits that count by nights.
        return (
            <div className="per-trip-dates-form per-section-group">
                <div className="rec-order-detail-subsection-title">
                    Entry &amp; Exit Dates
                </div>
                {description}
                <FlexRow className="per-date-entry">
                    <FlexCol md={4} lg={3}>
                        <div className="rec-form-item-wrap">
                            <VisualOnlyLabel>Entry Date</VisualOnlyLabel>
                            <div className="per-static-form-item" id="entryDate">
                                <DatePicker
                                    id="entryDateCalendar"
                                    value={gnomentToCalendarDate(entryDate)}
                                    showClearDates={false}
                                    isDisabled={true}
                                />
                            </div>
                        </div>
                    </FlexCol>
                    <FlexCol md={4} lg={3}>
                        {this.renderExitDateCalendar(entryDate, exitDate, newMap)}
                    </FlexCol>
                    {this.props.permit.id === DESOLATION_WILDERNESS_ID ? (
                        <FlexCol md={4} lg={3}>
                            <div className="rec-form-item-wrap">
                                <VisualOnlyLabel>Total Nights</VisualOnlyLabel>
                                <div className="per-static-form-item" id="totalNights">
                                    <Box paddingY="xs">
                                        <Text style={{ fontSize: '1.4rem' }}>
                                            {this.renderTotalDays() - 1}
                                        </Text>
                                    </Box>
                                </div>
                            </div>
                        </FlexCol>
                    ) : (
                        <FlexCol xs={4} lg={3}>
                            <div className="rec-form-item-wrap">
                                <VisualOnlyLabel>Total Days</VisualOnlyLabel>
                                <div className="per-static-form-item" id="totalDays">
                                    <Box paddingY="xs">
                                        <Text style={{ fontSize: '1.4rem' }}>
                                            {this.renderTotalDays()}
                                        </Text>
                                    </Box>
                                </div>
                            </div>
                        </FlexCol>
                    )}
                </FlexRow>
                {this.renderExtendedStay()}
                {this.renderChangeDetails()}
            </div>
        );
    }
}
